import React, { useState, useCallback, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { 
  Upload, 
  X, 
  Image, 
  FileText, 
  Trash2, 
  Move, 
  AlertCircle,
  CheckCircle,
  Info,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { uploadToCloudinary } from '../../utils/cloudinaryUpload';
import { getCloudinaryUrl } from '../../utils/imageUtils';
import '../../styles/SubmissionSystem.css';

const PortfolioUpload = ({ files, onChange, onNext, onPrevious, maxImages = 5 }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [errors, setErrors] = useState({});
  const [isValid, setIsValid] = useState(false);
  const [expandedCards, setExpandedCards] = useState(new Set());
  const [instructionsExpanded, setInstructionsExpanded] = useState(false);
  const [replacingImageId, setReplacingImageId] = useState(null);

  // Hidden file input for image replacement
  const fileInputRef = useRef(null);

  // Calculate remaining uploads
  const remainingUploads = maxImages - files.length;
  const canUploadMore = remainingUploads > 0;

  // Initialize expanded cards - only expand new files
  React.useEffect(() => {
    const newFiles = files.filter(file => file.isNew);
    const newExpandedCards = new Set();
    newFiles.forEach(file => {
      newExpandedCards.add(file.id);
    });
    setExpandedCards(newExpandedCards);
  }, []); // Only run once on component mount

  // Toggle card expansion
  const toggleCardExpansion = (fileId) => {
    const newExpanded = new Set(expandedCards);
    if (newExpanded.has(fileId)) {
      newExpanded.delete(fileId);
    } else {
      newExpanded.add(fileId);
    }
    setExpandedCards(newExpanded);
  };

  // Check if artwork is complete
  const getArtworkCompletionStatus = (file) => {
    const requiredFields = [
      file.artwork_title,
      file.artwork_medium,
      file.artwork_width,
      file.artwork_height
    ];
    
    const completedFields = requiredFields.filter(field => field && field.toString().trim() !== '').length;
    const totalFields = requiredFields.length;
    
    if (completedFields === 0) return 'incomplete';
    if (completedFields === totalFields) return 'complete';
    return 'partial';
  };

  // Get completion percentage
  const getCompletionPercentage = (file) => {
    const requiredFields = [
      file.artwork_title,
      file.artwork_medium,
      file.artwork_width,
      file.artwork_height
    ];
    
    const completedFields = requiredFields.filter(field => field && field.toString().trim() !== '').length;
    return (completedFields / requiredFields.length) * 100;
  };

  // Get required field badges
  const getRequiredFieldBadges = (file) => {
    const fields = [
      { key: 'title', label: 'Title', value: file.artwork_title },
      { key: 'medium', label: 'Medium', value: file.artwork_medium },
      { key: 'width', label: 'Width', value: file.artwork_width },
      { key: 'height', label: 'Height', value: file.artwork_height }
    ];
    
    return fields.map(field => ({
      ...field,
      status: field.value && field.value.toString().trim() !== '' ? 'complete' : 'incomplete'
    }));
  };

  // File validation
  const validateFiles = useCallback(() => {
    const newErrors = {};
    
    if (files.length === 0) {
      newErrors.files = 'Please upload at least one artwork image';
    }
    
    // Check if all files have required metadata
    files.forEach((file, index) => {
      if (!file.artwork_title?.trim()) {
        newErrors[`file_${index}_title`] = 'Artwork title is required';
      }
      if (!file.artwork_medium?.trim()) {
        newErrors[`file_${index}_medium`] = 'Artwork medium is required';
      }
      if (!file.artwork_width || !file.artwork_height) {
        newErrors[`file_${index}_width`] = 'Width is required';
        newErrors[`file_${index}_height`] = 'Height is required';
      }
    });
    
    setErrors(newErrors);
    setIsValid(Object.keys(newErrors).length === 0);
  }, [files]);

  React.useEffect(() => {
    validateFiles();
  }, [files, validateFiles]);

  // Auto-resize textareas when files change
  React.useEffect(() => {
    const textareas = document.querySelectorAll('.description-textarea');
    textareas.forEach(textarea => {
      textarea.style.height = 'auto';
      textarea.style.height = Math.max(120, textarea.scrollHeight) + 'px';
    });
  }, [files, expandedCards]);

  // Auto-expand newly added files and collapse existing ones by default
  React.useEffect(() => {
    const newFiles = files.filter(file => file.isNew);
    const existingFiles = files.filter(file => !file.isNew);
    
    // Expand all new files
    const newExpandedCards = new Set();
    newFiles.forEach(file => {
      newExpandedCards.add(file.id);
    });
    
    // Keep existing files collapsed by default (unless user manually expanded them)
    existingFiles.forEach(file => {
      if (expandedCards.has(file.id)) {
        newExpandedCards.add(file.id);
      }
    });
    
    setExpandedCards(newExpandedCards);
  }, [files.length]); // Only run when the number of files changes

  const onDrop = useCallback(async (acceptedFiles) => {
    if (!canUploadMore) {
      return; // Don't allow more uploads if limit reached
    }
    
    setUploading(true);
    
    try {
      const newFiles = [];
      
      // Limit the number of files that can be uploaded
      const filesToProcess = acceptedFiles.slice(0, remainingUploads);
      
      for (const file of filesToProcess) {
        // Validate file type and size
        if (!file.type.startsWith('image/')) {
          continue; // Skip non-image files
        }
        
        if (file.size > 10 * 1024 * 1024) { // 10MB limit
          continue; // Skip files that are too large
        }
        
        try {
          // Upload to Cloudinary
          const result = await uploadToCloudinary(file, 'image', 'submissions/portfolio');
          
          // Create file object with Cloudinary metadata
          const newFile = {
            id: Date.now() + Math.random(),
            filename: file.name,
            file_url: result.url,
            public_id: result.publicId,
            file_type: file.type,
            file_size: file.size,
            artwork_title: '',
            artwork_medium: '',
            artwork_width: '',
            artwork_height: '',
            artwork_depth: '',
            artwork_dimensions_unit: 'inches',
            artwork_year: new Date().getFullYear(),
            artwork_price: '',
            artwork_description: '',
            sort_order: files.length + newFiles.length,
            isNew: true, // Flag to identify newly uploaded files
            originalFile: file // Keep reference to original file for upload
          };
          
          newFiles.push(newFile);
        } catch (uploadError) {
          console.error('Error uploading file to Cloudinary:', uploadError);
          // Continue with other files even if one fails
        }
      }
      
      // Add new files to existing files
      onChange([...files, ...newFiles]);
      
    } catch (error) {
      console.error('Error processing files:', error);
    } finally {
      setUploading(false);
    }
  }, [files, onChange, canUploadMore, remainingUploads]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp']
    },
    maxSize: 10 * 1024 * 1024, // 10MB
    multiple: true
  });

  const handleFileRemove = (fileId) => {
    const updatedFiles = files.filter(file => file.id !== fileId);
    onChange(updatedFiles);
  };

  const handleFileReorder = (fromIndex, toIndex) => {
    const updatedFiles = [...files];
    const [movedFile] = updatedFiles.splice(fromIndex, 1);
    updatedFiles.splice(toIndex, 0, movedFile);
    
    // Update sort order
    updatedFiles.forEach((file, index) => {
      file.sort_order = index;
    });
    
    onChange(updatedFiles);
  };

  const handleFileMetadataChange = (fileId, field, value) => {
    const updatedFiles = files.map(file => 
      file.id === fileId ? { ...file, [field]: value } : file
    );
    onChange(updatedFiles);
  };

  const handleFileSave = (fileId) => {
    const updatedFiles = files.map(file => 
      file.id === fileId ? { ...file, isNew: false } : file
    );
    onChange(updatedFiles);
    validateFiles(); // Re-validate after saving
  };

  const handleImageReplace = async (fileId, newFile) => {
    try {
      setReplacingImageId(fileId);
      
      // Upload new image to Cloudinary
      const result = await uploadToCloudinary(newFile, 'image', 'submissions/portfolio');
      
      // Update the file with new image data while preserving metadata
      const updatedFiles = files.map(file => {
        if (file.id === fileId) {
          return {
            ...file,
            filename: newFile.name,
            file_url: result.url,
            public_id: result.publicId,
            file_type: newFile.type,
            file_size: newFile.size,
            isNew: false // Mark as saved since it's a replacement
          };
        }
        return file;
      });
      
      onChange(updatedFiles);
      console.log('Image replaced successfully');
      
    } catch (error) {
      console.error('Error replacing image:', error);
      // You could add error handling UI here
    } finally {
      setReplacingImageId(null);
    }
  };

  const triggerImageReplace = (fileId) => {
    setReplacingImageId(fileId);
    fileInputRef.current?.click();
    
    // Fallback cleanup: reset state after 5 seconds if no file is selected
    setTimeout(() => {
      if (replacingImageId === fileId) {
        setReplacingImageId(null);
      }
    }, 5000);
  };

  const handleFileInputChange = (event) => {
    const file = event.target.files[0];
    if (file && replacingImageId) {
      handleImageReplace(replacingImageId, file);
    }
    // Always reset the replacingImageId state, whether a file was selected or not
    setReplacingImageId(null);
    // Reset the input
    event.target.value = '';
  };

  const handleNext = () => {
    if (isValid) {
      onNext();
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="portfolio-upload-container">
      {/* Hidden file input for image replacement */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileInputChange}
        onClick={(e) => {
          // Reset replacingImageId when input is clicked (in case of cancellation)
          if (replacingImageId) {
            setReplacingImageId(null);
          }
        }}
        style={{ display: 'none' }}
      />
      
      {/* Upload Instructions */}
      <div className="portfolio-upload-content">
        {/* Header */}
        <div className="upload-header">
          <h2 className="upload-title">Portfolio Upload</h2>
          <p className="upload-main-instruction">
            Submit up to {maxImages} images.
          </p>
          <button 
            className="upload-instructions-toggle"
            onClick={() => setInstructionsExpanded(!instructionsExpanded)}
          >
            {instructionsExpanded ? (
              <>
                <span>Show Less</span>
                <ChevronUp className="w-4 h-4" />
              </>
            ) : (
              <>
                <span>Learn More</span>
                <ChevronDown className="w-4 h-4" />
              </>
            )}
          </button>
          
          {instructionsExpanded && (
            <div className="upload-instructions-content">
              <div className="upload-requirements">
                <div className="requirement-item">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span>Each image should be high resolution (at least 1920px on the longest side)</span>
                </div>
                <div className="requirement-item">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span>Supported formats: JPEG, PNG, GIF, WebP</span>
                </div>
                <div className="requirement-item">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span>Maximum file size: 10MB per image</span>
                </div>
                <div className="requirement-item">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span>Minimum resolution: 800x600 pixels</span>
                </div>
                <div className="requirement-item">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span>Please provide artwork details for each image</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Upload Progress */}
        {uploading && (
          <div className="upload-progress">
            <div className="upload-progress-header">
              <h4 className="upload-progress-title">Processing Files...</h4>
              <div className="upload-progress-bar">
                <div className="upload-progress-fill" style={{ width: '75%' }}></div>
              </div>
            </div>
          </div>
        )}

        {/* Uploaded Files */}
        {files.length > 0 && (
          <div className="uploaded-files">
            <div className="uploaded-files-header">
              <h3 className="uploaded-files-title">
                Uploaded Artworks ({files.length} of {maxImages})
              </h3>
              
              {/* Upload Countdown */}
              <div className="upload-countdown">
                <span className="countdown-text">
                  {remainingUploads > 0 
                    ? `${remainingUploads} more image${remainingUploads !== 1 ? 's' : ''} allowed`
                    : 'Maximum images reached'
                  }
                </span>
              </div>
              
              {/* Overall Progress Summary */}
              <div className="overall-progress">
                <div className="progress-summary">
                  <span className="progress-label">Overall Progress:</span>
                  <span className="progress-count">
                    {files.filter(f => getArtworkCompletionStatus(f) === 'complete').length} of {files.length} complete
                  </span>
                </div>
                <div className="progress-bar">
                  <div 
                    className="progress-fill" 
                    style={{ 
                      width: `${(files.filter(f => getArtworkCompletionStatus(f) === 'complete').length / files.length) * 100}%` 
                    }}
                  ></div>
                </div>
              </div>
            </div>
            
            {files.map((file, index) => {
              const isExpanded = expandedCards.has(file.id);
              const completionStatus = getArtworkCompletionStatus(file);
              const completionPercentage = getCompletionPercentage(file);
              const requiredFieldBadges = getRequiredFieldBadges(file);
              
              if (!isExpanded) {
                // Condensed card view
                return (
                  <div 
                    key={file.id} 
                    className="artwork-card-condensed"
                    onClick={() => toggleCardExpansion(file.id)}
                  >
                    <div className="condensed-content">
                      <img 
                        src={file.public_id ? getCloudinaryUrl(file.public_id, { width: 80, height: 80 }) : file.file_url} 
                        alt={file.artwork_title || `Artwork ${index + 1}`}
                        className="condensed-image"
                      />
                      
                      <div className="condensed-info">
                        <div className="condensed-title">
                          {file.artwork_title || `Untitled Artwork ${index + 1}`}
                        </div>
                        
                        <div className="condensed-status">
                          <div className={`status-indicator status-${completionStatus}`}>
                            {completionStatus === 'complete' && <CheckCircle className="w-3 h-3" />}
                            {completionStatus === 'partial' && <AlertCircle className="w-3 h-3" />}
                            {completionStatus === 'incomplete' && <X className="w-3 h-3" />}
                            {completionStatus === 'complete' ? 'Complete' : 
                             completionStatus === 'partial' ? 'Partial' : 'Incomplete'}
                          </div>
                          
                          <div className="artwork-progress">
                            <span className="progress-text">{Math.round(completionPercentage)}%</span>
                            <div className="progress-bar">
                              <div 
                                className="progress-fill" 
                                style={{ width: `${completionPercentage}%` }}
                              ></div>
                            </div>
                          </div>
                        </div>
                        
                        <div className="required-fields-indicator">
                          {requiredFieldBadges.map(field => (
                            <span 
                              key={field.key} 
                              className={`required-field-badge ${field.status}`}
                            >
                              {field.label}
                            </span>
                          ))}
                        </div>
                      </div>
                      
                      <button 
                        className="expand-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleCardExpansion(file.id);
                        }}
                      >
                        <ChevronDown className="w-4 h-4 expand-icon" />
                        Edit Details
                      </button>
                    </div>
                  </div>
                );
              } else {
                // Expanded card view
                return (
                  <div key={file.id} className="artwork-card-expanded">
                    <div className="expanded-header">
                      <div className="expanded-title">
                        {file.artwork_title || `Untitled Artwork ${index + 1}`}
                      </div>
                      <button 
                        className="collapse-button"
                        onClick={() => toggleCardExpansion(file.id)}
                      >
                        <ChevronUp className="w-4 h-4" />
                        Collapse
                      </button>
                    </div>
                    
                    <div className="uploaded-file-card">
                      <div className="file-preview">
                        <img 
                          src={file.public_id ? getCloudinaryUrl(file.public_id, { width: 300, height: 300 }) : file.file_url} 
                          alt={file.artwork_title || `Artwork ${index + 1}`}
                          className="file-preview-image"
                        />
                      </div>
                      
                      <div className="file-metadata-form">
                        {/* Title - Full width */}
                        <div className="form-field full-width">
                          <label className="field-label">Artwork Title *</label>
                          <input
                            type="text"
                            placeholder="Enter artwork title"
                            value={file.artwork_title || ''}
                            onChange={(e) => handleFileMetadataChange(file.id, 'artwork_title', e.target.value)}
                            className={`form-input ${errors[`file_${index}_title`] ? 'error' : ''}`}
                            required
                          />
                          {errors[`file_${index}_title`] && (
                            <span className="field-error">{errors[`file_${index}_title`]}</span>
                          )}
                        </div>

                        {/* Medium - Full width */}
                        <div className="form-field full-width">
                          <label className="field-label">Medium *</label>
                          <input
                            type="text"
                            placeholder="e.g., Oil on canvas, Digital print, etc."
                            value={file.artwork_medium || ''}
                            onChange={(e) => handleFileMetadataChange(file.id, 'artwork_medium', e.target.value)}
                            className={`form-input ${errors[`file_${index}_medium`] ? 'error' : ''}`}
                            required
                          />
                          {errors[`file_${index}_medium`] && (
                            <span className="field-error">{errors[`file_${index}_medium`]}</span>
                          )}
                        </div>

                        {/* Dimensions - Full width container */}
                        <div className="form-field full-width dimensions-container">
                          <label className="field-label">Dimensions *</label>
                          <div className="dimensions-grid">
                            <div className="dimension-input">
                              <input
                                type="number"
                                placeholder="W"
                                value={file.artwork_width || ''}
                                onChange={(e) => handleFileMetadataChange(file.id, 'artwork_width', e.target.value)}
                                className={`form-input ${errors[`file_${index}_width`] ? 'error' : ''}`}
                                step="0.01"
                                min="0"
                                required
                              />
                              {errors[`file_${index}_width`] && (
                                <span className="field-error">{errors[`file_${index}_width`]}</span>
                              )}
                            </div>

                            <span className="dimension-separator">×</span>

                            <div className="dimension-input">
                              <input
                                type="number"
                                placeholder="H"
                                value={file.artwork_height || ''}
                                onChange={(e) => handleFileMetadataChange(file.id, 'artwork_height', e.target.value)}
                                className={`form-input ${errors[`file_${index}_height`] ? 'error' : ''}`}
                                step="0.01"
                                min="0"
                                required
                              />
                              {errors[`file_${index}_height`] && (
                                <span className="field-error">{errors[`file_${index}_height`]}</span>
                              )}
                            </div>

                            <select
                              value={file.artwork_dimensions_unit || 'inches'}
                              onChange={(e) => handleFileMetadataChange(file.id, 'artwork_dimensions_unit', e.target.value)}
                              className="form-select"
                            >
                              <option value="inches">inches</option>
                              <option value="cm">cm</option>
                              <option value="mm">mm</option>
                            </select>
                          </div>
                        </div>

                        {/* Price - Left column */}
                        <div className="form-field price-container">
                          <label className="field-label">Price (Optional)</label>
                          <div className="price-input-wrapper">
                            <span className="price-currency">$</span>
                            <input
                              type="number"
                              placeholder="0.00"
                              value={file.artwork_price || ''}
                              onChange={(e) => handleFileMetadataChange(file.id, 'artwork_price', e.target.value)}
                              className="form-input price-input"
                              step="0.01"
                              min="0"
                            />
                          </div>
                        </div>

                        {/* Year - Right column */}
                        <div className="form-field year-container">
                          <label className="field-label">Year Created</label>
                          <input
                            type="number"
                            placeholder="2024"
                            value={file.artwork_year || ''}
                            onChange={(e) => handleFileMetadataChange(file.id, 'artwork_year', e.target.value)}
                            className="form-input"
                            min="1900"
                            max={new Date().getFullYear() + 1}
                          />
                        </div>

                        {/* Description - Full width */}
                        <div className="form-field full-width">
                          <label className="field-label">Artwork Description (Optional)</label>
                          <textarea
                            placeholder="Brief description of the artwork, inspiration, or techniques used..."
                            value={file.artwork_description || ''}
                            onChange={(e) => {
                              handleFileMetadataChange(file.id, 'artwork_description', e.target.value);
                              // Auto-resize textarea
                              const textarea = e.target;
                              textarea.style.height = 'auto';
                              textarea.style.height = Math.max(120, textarea.scrollHeight) + 'px';
                            }}
                            className="form-textarea description-textarea"
                            rows="3"
                            maxLength="500"
                          />
                          <div className="character-count">
                            {(file.artwork_description || '').length}/500 characters
                          </div>
                        </div>

                        {/* Actions - Full width with save and remove */}
                        <div className="form-actions">
                          <div className="action-buttons-row">
                                                         <button
                               type="button"
                               onClick={() => handleFileSave(file.id)}
                               className={`save-button ${!file.isNew ? 'saved' : ''}`}
                               disabled={!file.isNew}
                             >
                               {file.isNew ? (
                                 <>
                                   <CheckCircle className="w-4 h-4" />
                                   Save Artwork
                                 </>
                               ) : (
                                 <>
                                   <CheckCircle className="w-4 h-4" />
                                   Saved ✓
                                 </>
                               )}
                             </button>

                             <button
                               type="button"
                               onClick={() => triggerImageReplace(file.id)}
                               className="replace-button"
                               disabled={replacingImageId === file.id}
                             >
                               {replacingImageId === file.id ? (
                                 <>
                                   <Upload className="w-4 h-4" />
                                   Replacing...
                                 </>
                               ) : (
                                 <>
                                   <Upload className="w-4 h-4" />
                                   Replace Image
                                 </>
                               )}
                             </button>

                             <button
                               type="button"
                               onClick={() => handleFileRemove(file.id)}
                               className="remove-button"
                             >
                               <Trash2 className="w-4 h-4" />
                               Remove Artwork
                             </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }
            })}
          </div>
        )}

        {/* Upload Zone - Now below images for better UX */}
        <div
          {...getRootProps()}
          className={`upload-zone ${isDragActive ? 'dragover' : ''} ${!canUploadMore ? 'disabled' : ''}`}
        >
          <input {...getInputProps()} />
          <div className="upload-placeholder">
            <Upload className="w-12 h-12 text-gray-400 mb-4" />
            <p className="upload-placeholder-text">
              {canUploadMore 
                ? `Drag & drop images here, or click to select (${remainingUploads} more allowed)`
                : 'Maximum number of images reached'
              }
            </p>
            <p className="upload-specs">JPG, PNG up to 10MB each</p>
            {!canUploadMore && (
              <p className="upload-limit-reached">
                You can remove existing images to upload new ones
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortfolioUpload;
